import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Truck, Package, MapPin, Clock, CheckCircle, 
  AlertCircle, LogOut, User, Calendar, ChevronRight,
  FileText, Paperclip, Plus, Trash2
} from "lucide-react";

const LOGO_URL = "/nassau-logo.png";

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    in_progress: "bg-blue-100 text-blue-800 border-blue-200",
    completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
    failed: "bg-red-100 text-red-800 border-red-200"
  };
  
  const labels = {
    pending: "Pending",
    in_progress: "In Progress",
    completed: "Completed",
    failed: "Failed"
  };
  
  return (
    <Badge variant="outline" className={`${styles[status]} font-medium`}>
      {labels[status]}
    </Badge>
  );
};

export default function DriverDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("today");
  
  // Job creation states
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [newCustomerDialogOpen, setNewCustomerDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const driverDocInputRef = useRef(null);
  
  const [jobFormData, setJobFormData] = useState({
    customer_id: "",
    delivery_address: "",
    delivery_date: new Date().toISOString().split('T')[0],
    time_window: "",
    items_description: "",
    special_instructions: ""
  });
  
  const [newCustomerData, setNewCustomerData] = useState({
    name: "",
    address: "",
    city: "",
    county: "",
    postcode: "",
    country: "",
    email: "",
    phone: ""
  });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      const [jobsRes, statsRes, customersRes] = await Promise.all([
        api.get("/jobs", {
          params: filter === "today" ? { date: new Date().toISOString().split('T')[0] } : {}
        }),
        api.get("/stats"),
        api.get("/customers")
      ]);
      setJobs(jobsRes.data);
      setStats(statsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const startDelivery = async (job) => {
    if (job.status === "pending") {
      try {
        await api.put(`/jobs/${job.id}/status?status=in_progress`);
        toast.success("Delivery started");
      } catch (error) {
        toast.error("Failed to start delivery");
        return;
      }
    }
    navigate(`/driver/deliver/${job.id}`);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setJobFormData({
      ...jobFormData,
      customer_id: customerId,
      delivery_address: customer?.address || ""
    });
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    if (!jobFormData.customer_id || !jobFormData.delivery_address || !jobFormData.delivery_date) {
      toast.error("Customer, address, and date are required");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post("/jobs", jobFormData);
      const newJobId = response.data.id;
      
      // Upload any pending documents
      if (pendingDocuments.length > 0) {
        for (const file of pendingDocuments) {
          const docFormData = new FormData();
          docFormData.append("file", file);
          try {
            await api.post(`/jobs/${newJobId}/documents`, docFormData, {
              headers: { "Content-Type": "multipart/form-data" }
            });
          } catch (docError) {
            console.error("Failed to upload document:", docError);
          }
        }
      }
      
      toast.success("Delivery job created and assigned to you");
      setJobDialogOpen(false);
      setJobFormData({
        customer_id: "",
        delivery_address: "",
        delivery_date: new Date().toISOString().split('T')[0],
        time_window: "",
        items_description: "",
        special_instructions: ""
      });
      setPendingDocuments([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create job");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDriverDocAdd = (e) => {
    const files = Array.from(e.target.files || []);
    setPendingDocuments([...pendingDocuments, ...files]);
    if (driverDocInputRef.current) {
      driverDocInputRef.current.value = "";
    }
  };

  const removeDriverPendingDoc = (index) => {
    setPendingDocuments(pendingDocuments.filter((_, i) => i !== index));
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerData.name || !newCustomerData.address) {
      toast.error("Customer name and address are required");
      return;
    }
    
    setCreatingCustomer(true);
    try {
      const response = await api.post("/customers", newCustomerData);
      toast.success("Customer created successfully");
      
      setCustomers([...customers, response.data]);
      setJobFormData({
        ...jobFormData,
        customer_id: response.data.id,
        delivery_address: response.data.address
      });
      
      setNewCustomerDialogOpen(false);
      setNewCustomerData({
        name: "",
        address: "",
        city: "",
        county: "",
        postcode: "",
        country: "",
        email: "",
        phone: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create customer");
    } finally {
      setCreatingCustomer(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  const pendingJobs = jobs.filter(j => j.status === "pending" || j.status === "in_progress");
  const completedJobs = jobs.filter(j => j.status === "completed");

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="driver-dashboard">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 border-b border-slate-200">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <img src={LOGO_URL} alt="Nassau Engineering" className="h-10" />
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700">{user?.full_name}</span>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-5 w-5 text-slate-600" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="bg-[#003366] text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Package className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold tabular-nums">{stats?.todays_jobs || 0}</p>
                  <p className="text-sm opacity-80">Today's Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-emerald-600 text-white border-0">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <CheckCircle className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-2xl font-bold tabular-nums">{stats?.todays_completed || 0}</p>
                  <p className="text-sm opacity-80">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            variant={filter === "today" ? "default" : "outline"}
            onClick={() => setFilter("today")}
            className={filter === "today" ? "bg-[#003366] hover:bg-[#002244]" : ""}
            data-testid="filter-today"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Today
          </Button>
          <Button
            variant={filter === "all" ? "default" : "outline"}
            onClick={() => setFilter("all")}
            className={filter === "all" ? "bg-[#003366] hover:bg-[#002244]" : ""}
            data-testid="filter-all"
          >
            <Package className="h-4 w-4 mr-2" />
            All Jobs
          </Button>
          <Button
            onClick={() => setJobDialogOpen(true)}
            className="bg-[#D32F2F] hover:bg-[#B71C1C] ml-auto"
            data-testid="new-job-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Job
          </Button>
        </div>

        {/* Pending Jobs */}
        {pendingJobs.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Pending Deliveries ({pendingJobs.length})
            </h2>
            {pendingJobs.map((job) => (
              <Card 
                key={job.id} 
                className="card-nassau cursor-pointer"
                onClick={() => startDelivery(job)}
                data-testid={`job-card-${job.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="space-y-1">
                      <p className="font-mono text-sm text-[#003366] font-medium">{job.job_number}</p>
                      <h3 className="font-semibold text-slate-900">{job.customer_name}</h3>
                    </div>
                    <StatusBadge status={job.status} />
                  </div>
                  
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" />
                      <span>{job.delivery_address}</span>
                    </div>
                    {job.time_window && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-slate-400" />
                        <span>{job.time_window}</span>
                      </div>
                    )}
                    {job.items_description && (
                      <div className="flex items-start gap-2 bg-blue-50 p-2 rounded-md mt-2">
                        <Package className="h-4 w-4 mt-0.5 text-blue-600 flex-shrink-0" />
                        <span className="text-blue-800 font-medium">{job.items_description}</span>
                      </div>
                    )}
                    {job.documents && job.documents.length > 0 && (
                      <div className="flex items-center gap-2 text-blue-600">
                        <Paperclip className="h-4 w-4" />
                        <span>{job.documents.length} document{job.documents.length > 1 ? 's' : ''} attached</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <Button 
                      size="sm" 
                      className="bg-[#D32F2F] hover:bg-[#B71C1C]"
                      data-testid={`start-delivery-${job.id}`}
                    >
                      {job.status === "pending" ? "Start Delivery" : "Continue"}
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Completed Jobs */}
        {completedJobs.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
              Completed ({completedJobs.length})
            </h2>
            {completedJobs.map((job) => (
              <Card 
                key={job.id} 
                className="card-nassau opacity-75"
                data-testid={`completed-job-${job.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <p className="font-mono text-sm text-slate-500">{job.job_number}</p>
                      <h3 className="font-medium text-slate-700">{job.customer_name}</h3>
                      <p className="text-sm text-slate-500">{job.delivery_address}</p>
                    </div>
                    <StatusBadge status={job.status} />
                  </div>
                  {job.proof_id && (
                    <div className="mt-3 flex justify-end">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/driver/docket/${job.proof_id}`);
                        }}
                        data-testid={`view-docket-${job.id}`}
                      >
                        View Docket
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Empty State */}
        {jobs.length === 0 && (
          <div className="text-center py-12">
            <Truck className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600">No deliveries found</h3>
            <p className="text-slate-500 mb-4">Create a new delivery job to get started</p>
            <Button 
              onClick={() => setJobDialogOpen(true)}
              className="bg-[#D32F2F] hover:bg-[#B71C1C]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create New Job
            </Button>
          </div>
        )}
      </main>

      {/* Create Job Dialog */}
      <Dialog open={jobDialogOpen} onOpenChange={setJobDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Delivery Job</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateJob} className="space-y-4">
            <div className="space-y-2">
              <Label>Customer *</Label>
              <div className="flex gap-2">
                <Select value={jobFormData.customer_id} onValueChange={handleCustomerChange}>
                  <SelectTrigger data-testid="driver-customer-select" className="flex-1">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setNewCustomerDialogOpen(true)}
                  data-testid="driver-new-customer-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  New
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="driver-address">Delivery Address *</Label>
              <Input
                id="driver-address"
                value={jobFormData.delivery_address}
                onChange={(e) => setJobFormData({ ...jobFormData, delivery_address: e.target.value })}
                placeholder="Full delivery address"
                data-testid="driver-job-address"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="driver-date">Delivery Date *</Label>
                <Input
                  id="driver-date"
                  type="date"
                  value={jobFormData.delivery_date}
                  onChange={(e) => setJobFormData({ ...jobFormData, delivery_date: e.target.value })}
                  data-testid="driver-job-date"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver-time">Time Window</Label>
                <Input
                  id="driver-time"
                  value={jobFormData.time_window}
                  onChange={(e) => setJobFormData({ ...jobFormData, time_window: e.target.value })}
                  placeholder="e.g., 9AM - 12PM"
                  data-testid="driver-job-time"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="driver-items">Items Description</Label>
              <Textarea
                id="driver-items"
                value={jobFormData.items_description}
                onChange={(e) => setJobFormData({ ...jobFormData, items_description: e.target.value })}
                placeholder="What's being delivered...

Example:
- 2x Steel Beams
- 1x Control Panel"
                className="min-h-[120px]"
                data-testid="driver-job-items"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="driver-instructions">Special Instructions</Label>
              <Textarea
                id="driver-instructions"
                value={jobFormData.special_instructions}
                onChange={(e) => setJobFormData({ ...jobFormData, special_instructions: e.target.value })}
                placeholder="Access codes, delivery notes, etc."
                data-testid="driver-job-instructions"
              />
            </div>

            {/* Document Upload Section */}
            <div className="space-y-2">
              <Label>Attach Documents</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4">
                <input
                  ref={driverDocInputRef}
                  type="file"
                  onChange={handleDriverDocAdd}
                  className="hidden"
                  id="driver-job-doc-upload"
                  multiple
                  data-testid="driver-job-doc-input"
                />
                <label htmlFor="driver-job-doc-upload" className="cursor-pointer flex flex-col items-center">
                  <FileText className="h-8 w-8 text-slate-400 mb-2" />
                  <span className="text-sm text-slate-600">Click to add documents</span>
                </label>
              </div>
              {pendingDocuments.length > 0 && (
                <div className="space-y-2 mt-2">
                  {pendingDocuments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-700 truncate max-w-[180px]">{file.name}</span>
                        <span className="text-xs text-slate-500">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDriverPendingDoc(index)}
                        className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setJobDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#003366] hover:bg-[#002244]"
                data-testid="driver-save-job-btn"
              >
                {submitting ? "Creating..." : "Create Job"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Customer Dialog */}
      <Dialog open={newCustomerDialogOpen} onOpenChange={setNewCustomerDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="driver-customer-name">Customer Name *</Label>
                <Input
                  id="driver-customer-name"
                  value={newCustomerData.name}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                  placeholder="Company or individual name"
                  data-testid="driver-customer-name"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="driver-customer-address">Address *</Label>
                <Input
                  id="driver-customer-address"
                  value={newCustomerData.address}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, address: e.target.value })}
                  placeholder="Street address"
                  data-testid="driver-customer-address"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver-customer-city">Town/City</Label>
                <Input
                  id="driver-customer-city"
                  value={newCustomerData.city}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, city: e.target.value })}
                  placeholder="Town/City"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver-customer-county">County</Label>
                <Input
                  id="driver-customer-county"
                  value={newCustomerData.county}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, county: e.target.value })}
                  placeholder="County"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver-customer-postcode">Postal Code</Label>
                <Input
                  id="driver-customer-postcode"
                  value={newCustomerData.postcode}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, postcode: e.target.value })}
                  placeholder="Postal Code"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver-customer-country">Country</Label>
                <Input
                  id="driver-customer-country"
                  value={newCustomerData.country}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, country: e.target.value })}
                  placeholder="Country"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver-customer-email">Email</Label>
                <Input
                  id="driver-customer-email"
                  type="email"
                  value={newCustomerData.email}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver-customer-phone">Phone</Label>
                <Input
                  id="driver-customer-phone"
                  value={newCustomerData.phone}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                  placeholder="+1234567890"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCustomerDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateCustomer}
              disabled={creatingCustomer}
              className="bg-[#003366] hover:bg-[#002244]"
              data-testid="driver-save-customer-btn"
            >
              {creatingCustomer ? "Creating..." : "Create Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <nav className="mobile-nav bg-white border-t border-slate-200 h-16">
        <div className="max-w-md mx-auto h-full flex items-center justify-around">
          <button className="flex flex-col items-center gap-1 text-[#003366]" data-testid="nav-jobs">
            <Package className="h-6 w-6" />
            <span className="text-xs font-medium">Jobs</span>
          </button>
          <button 
            className="flex flex-col items-center gap-1 text-[#D32F2F]" 
            onClick={() => setJobDialogOpen(true)}
            data-testid="nav-new-job"
          >
            <Plus className="h-6 w-6" />
            <span className="text-xs font-medium">New Job</span>
          </button>
          <button className="flex flex-col items-center gap-1 text-slate-400" data-testid="nav-profile">
            <User className="h-6 w-6" />
            <span className="text-xs">Profile</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
